import { Ban, CircleCheck, CircleX, LoaderCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isContactImportJobExecutionStatus } from "@multi-chat/shared";
import { cn } from "../../lib/utils.js";
import { useNotifications } from "./notification-context.js";
import type { AppNotification } from "./notification-types.js";

export function NotificationStack({
  onOpenImport,
}: {
  onOpenImport?: (jobId: string) => void;
}): React.JSX.Element | null {
  const { visible, dismissNotification } = useNotifications();
  if (visible.length === 0) return null;

  return (
    <div
      className="pointer-events-none absolute z-[var(--z-popover)] top-[max(1rem,env(safe-area-inset-top,0px))] inset-inline-end-4 flex w-max max-w-[min(24rem,calc(100%-2rem))] flex-col gap-2"
      data-testid="app-notification-stack"
    >
      {visible.map((item) => (
        <NotificationCard
          key={item.id}
          item={item}
          onDismiss={() => dismissNotification(item.id)}
          onOpenImport={onOpenImport}
        />
      ))}
    </div>
  );
}

function NotificationCard({
  item,
  onDismiss,
  onOpenImport,
}: {
  item: AppNotification;
  onDismiss: () => void;
  onOpenImport?: (jobId: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const isImport = item.kind === "import";
  const isError = item.kind === "error";
  const total = item.totalRows ?? 0;
  const processed = item.processedRows ?? 0;
  const progress = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const importInProgress = isImport && item.status != null && isContactImportJobExecutionStatus(item.status);
  const importFailed = isImport && item.status === "FAILED";
  const importCancelled = isImport && item.status === "CANCELLED";
  const toastIconWrapClass = importFailed || isError
    ? "bg-red-50 text-red-600"
    : importCancelled
      ? "bg-surface-muted text-muted"
      : "bg-success-muted text-success";

  return (
    <div
      className={cn(
        "pointer-events-auto relative w-full overflow-hidden rounded-xl border border-line/80 bg-surface-elevated text-start shadow-lg animate-toast-in motion-reduce:animate-none",
        isImport && item.jobId ? "cursor-pointer" : null,
      )}
      role="status"
      aria-live="polite"
      data-testid={isImport ? "contact-import-toast" : "app-notification-toast"}
      onClick={() => {
        if (isImport && item.jobId) {
          onDismiss();
          onOpenImport?.(item.jobId);
        }
      }}
    >
      <div className="flex items-start gap-3 px-3.5 py-3">
        <span
          className={cn(
            "mt-0.5 inline-grid size-8 shrink-0 place-items-center rounded-full",
            toastIconWrapClass,
          )}
        >
          {importInProgress ? (
            <LoaderCircle size={18} strokeWidth={2.25} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : importFailed || isError ? (
            <CircleX size={18} strokeWidth={2.25} aria-hidden="true" />
          ) : importCancelled ? (
            <Ban size={16} strokeWidth={2.25} aria-hidden="true" />
          ) : (
            <CircleCheck size={18} strokeWidth={2.25} aria-hidden="true" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="m-0 pt-1 text-[0.875rem] font-semibold leading-snug tracking-[-0.01em] text-foreground">
            {item.message}
          </p>
          {item.detail ? (
            <p className="m-0 mt-0.5 text-[0.75rem] font-normal leading-snug text-muted">
              {item.detail}
            </p>
          ) : null}
          {importInProgress && total > 0 ? (
            <p className="m-0 mt-0.5 text-[0.75rem] font-normal text-muted" data-testid="contact-import-toast-progress">
              {t("contacts.import.job.progressCounts", {
                processed: processed.toLocaleString(i18n.language),
                total: total.toLocaleString(i18n.language),
                created: (item.createdCount ?? 0).toLocaleString(i18n.language),
                restored: (item.restoredCount ?? 0).toLocaleString(i18n.language),
              })}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="inline-grid size-7 shrink-0 place-items-center rounded-md text-muted transition-colors hover:bg-surface-muted hover:text-foreground"
          aria-label={t("common.close")}
          data-testid="app-notification-dismiss"
          onClick={(event) => {
            event.stopPropagation();
            onDismiss();
          }}
        >
          <X size={14} strokeWidth={2.25} aria-hidden="true" />
        </button>
      </div>
      {importInProgress ? (
        <div className="h-[3px] w-full bg-success-muted" aria-hidden="true">
          <div className="h-full bg-success" style={{ width: `${progress}%` }} />
        </div>
      ) : item.durationMs != null ? (
        <div className="h-[3px] w-full bg-success-muted" aria-hidden="true">
          <div
            className="h-full w-full bg-success"
            style={{
              transformOrigin: "inline-start",
              animationName: "contacts-toast-progress",
              animationDuration: `${item.durationMs}ms`,
              animationTimingFunction: "linear",
              animationFillMode: "forwards",
            }}
          />
        </div>
      ) : (
        <div className="h-[3px] w-full bg-success-muted" aria-hidden="true" />
      )}
    </div>
  );
}
